import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, MapPin, Briefcase, GraduationCap, Crown, Shield, User, MessageSquare, Clock, X, Lock } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

// Cache for template to avoid repeated API calls
let cachedTemplate = null;
let cacheTimestamp = 0;
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const fetchActiveTemplate = async () => {
  const now = Date.now();
  if (cachedTemplate && (now - cacheTimestamp) < CACHE_DURATION) {
    return cachedTemplate;
  }

  try {
    const response = await fetch(`${API_URL}/api/admin/profile-card-design/active/public`);
    if (response.ok) {
      const data = await response.json();
      cachedTemplate = data.template;
      cacheTimestamp = now;
      return cachedTemplate;
    }
  } catch (error) {
    console.error('Failed to fetch card template:', error);
  }

  // Return default template if fetch fails
  return {
    layout: 'vertical',
    photo_shape: 'rounded',
    photo_size: 'large',
    background_color: '#ffffff',
    text_color: '#1f2937',
    accent_color: '#10b981',
    border_color: '#e5e7eb',
    name_font_size: 'lg',
    name_font_weight: 'semibold',
    detail_font_size: 'sm',
    border_radius: 'xl',
    shadow: 'md',
    border_width: '1',
    padding: '4',
    show_premium_badge: true,
    show_verified_badge: true,
    show_online_status: true,
    show_bio: true,
    bio_max_lines: 2,
    show_interest_button: true,
    button_style: 'filled',
    fields: [
      { field_id: 'age', enabled: true, order: 1 },
      { field_id: 'location', enabled: true, order: 2 },
      { field_id: 'profession', enabled: true, order: 3 },
    ]
  };
};

// Force refresh template cache (call after admin saves new template)
export const refreshTemplateCache = () => {
  cachedTemplate = null;
  cacheTimestamp = 0;
};

const DynamicProfileCard = ({ 
  profile, 
  onInterestClick, 
  onCardClick,
  isInterestSent = false,
  isInterestReceived = false,
  isMutualMatch = false,
  sending = false,
  isPremium = true,
  onUpgradeClick,
  isShortlisted = false,
  onShortlistToggle,
  hidePremiumBadge = false,
  className = ''
}) => {
  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showUpgradePrompt, setShowUpgradePrompt] = useState(false);
  const [shortlisted, setShortlisted] = useState(isShortlisted);
  const [shortlistLoading, setShortlistLoading] = useState(false);
  const navigate = useNavigate();

  // Update local shortlist state when prop changes
  useEffect(() => {
    setShortlisted(isShortlisted);
  }, [isShortlisted]);

  useEffect(() => {
    const loadTemplate = async () => {
      const t = await fetchActiveTemplate();
      setTemplate(t);
      setLoading(false);
    };
    loadTemplate();
  }, []);

  if (loading || !template) {
    return (
      <div className={`bg-white rounded-xl shadow-md animate-pulse ${className}`}>
        <div className="h-48 bg-gray-200 rounded-t-xl" />
        <div className="p-4 space-y-3">
          <div className="h-5 bg-gray-200 rounded w-3/4" />
          <div className="h-4 bg-gray-200 rounded w-1/2" />
          <div className="h-4 bg-gray-200 rounded w-2/3" />
        </div>
      </div>
    );
  }

  // Helper functions
  const getPhotoSizeClass = () => {
    switch (template.photo_size) {
      case 'small': return 'h-32';
      case 'large': return 'h-48';
      case 'full': return 'h-64';
      default: return 'h-40';
    }
  };

  const getPhotoShapeClass = () => {
    if (template.layout === 'photo_overlay') return '';
    switch (template.photo_shape) {
      case 'circle': return 'rounded-full mx-auto w-32 h-32';
      case 'square': return 'rounded-none';
      default: return 'rounded-xl';
    }
  };

  const getBorderRadiusClass = () => {
    switch (template.border_radius) {
      case 'none': return 'rounded-none';
      case 'sm': return 'rounded-sm';
      case 'md': return 'rounded-md';
      case 'lg': return 'rounded-lg';
      case 'xl': return 'rounded-xl';
      case 'full': return 'rounded-3xl';
      default: return 'rounded-lg';
    }
  };

  const getShadowClass = () => {
    switch (template.shadow) {
      case 'none': return 'shadow-none';
      case 'sm': return 'shadow-sm';
      case 'lg': return 'shadow-lg';
      case 'xl': return 'shadow-xl';
      default: return 'shadow-md';
    }
  };

  const getNameSizeClass = () => {
    switch (template.name_font_size) {
      case 'sm': return 'text-sm';
      case 'md': return 'text-base';
      case 'xl': return 'text-xl';
      default: return 'text-lg';
    }
  };

  const getNameWeightClass = () => {
    switch (template.name_font_weight) {
      case 'normal': return 'font-normal';
      case 'medium': return 'font-medium';
      case 'bold': return 'font-bold';
      default: return 'font-semibold';
    }
  };

  const getDetailSizeClass = () => {
    switch (template.detail_font_size) {
      case 'xs': return 'text-xs';
      case 'md': return 'text-base';
      default: return 'text-sm';
    }
  };

  const enabledFields = (template.fields || [])
    .filter(f => f.enabled)
    .sort((a, b) => a.order - b.order);

  const getFieldValue = (fieldId) => {
    switch (fieldId) {
      case 'age': return profile.age ? `${profile.age} years` : null;
      case 'location': 
        if (profile.location) return profile.location;
        if (profile.province && profile.country) return `${profile.province}, ${profile.country}`;
        return null;
      case 'profession': return profile.occupation || profile.profession;
      case 'education': return profile.education;
      case 'religion': return profile.religion;
      default: return null;
    }
  };

  const getFieldIcon = (fieldId) => {
    switch (fieldId) {
      case 'age': return User;
      case 'location': return MapPin;
      case 'profession': return Briefcase;
      case 'education': return GraduationCap;
      case 'religion': return Heart;
      default: return User;
    }
  };

  const profileImage = profile.image || profile.profileImage || profile.photos?.[0] || 
    `https://ui-avatars.com/api/?name=${profile.firstName || profile.name || 'User'}&size=400&background=random`;

  const profileName = profile.firstName ? 
    `${profile.firstName}${profile.lastName ? ' ' + profile.lastName : ''}` : 
    (profile.name || 'User');

  const handleCardClick = () => {
    if (onCardClick) {
      onCardClick(profile);
    } else {
      navigate(`/profile/${profile.id || profile._id}`);
    }
  };

  const handleShortlistClick = async (e) => {
    e.stopPropagation();
    if (shortlistLoading) return;
    
    setShortlistLoading(true);
    try {
      if (onShortlistToggle) {
        await onShortlistToggle(profile.id || profile._id, !shortlisted);
      }
      setShortlisted(!shortlisted);
    } catch (error) {
      console.error('Error toggling shortlist:', error);
    } finally {
      setShortlistLoading(false);
    }
  };

  const handleInterestClick = (e) => {
    e.stopPropagation();
    
    // Check if user is premium before allowing ANY action (including messaging)
    // Non-premium users must upgrade to send interests, accept interests, or message
    if (!isPremium) {
      setShowUpgradePrompt(true);
      return;
    }
    
    // Allow click for: sending interest, accepting received interest, or messaging mutual match
    if (onInterestClick) {
      if (isMutualMatch) {
        // Navigate to messages when it's a mutual match
        navigate(`/messages/${profile.id || profile._id}`);
      } else if (isInterestReceived) {
        // Accept the received interest
        onInterestClick(profile.id || profile._id);
      } else if (!isInterestSent) {
        // Send new interest
        onInterestClick(profile.id || profile._id);
      }
    }
  };

  const handleUpgradeClick = () => {
    setShowUpgradePrompt(false);
    if (onUpgradeClick) {
      onUpgradeClick();
    } else {
      navigate('/pricing');
    }
  };

  const getButtonText = () => {
    // Non-premium users always see upgrade text (except for pending interests they sent before)
    if (!isPremium && !isInterestSent) {
      return 'Upgrade to Connect';
    }
    if (isMutualMatch) return 'Send Message';
    if (isInterestSent) return 'Pending';
    if (isInterestReceived) return 'Accept Interest';
    return 'Send Interest';
  };

  // Get button styling based on interest state
  const getButtonStyles = () => {
    // Non-premium users - show upgrade prompt style (except for pending interests)
    if (!isPremium && !isInterestSent) {
      return {
        backgroundColor: '#fbbf24', // Amber/gold for upgrade
        color: '#78350f', // Dark amber text
        cursor: 'pointer'
      };
    }
    // Mutual match - green/teal color for "Send Message"
    if (isMutualMatch) {
      return {
        backgroundColor: '#10b981', // Green/teal
        color: '#ffffff',
        cursor: 'pointer'
      };
    }
    // Pending - light yellow background
    if (isInterestSent) {
      return {
        backgroundColor: '#fef3c7', // Light yellow
        color: '#92400e', // Dark amber text
        cursor: 'not-allowed'
      };
    }
    // Received interest - accent color to accept
    if (isInterestReceived) {
      return {
        backgroundColor: template.accent_color || '#10b981',
        color: '#ffffff',
        cursor: 'pointer'
      };
    }
    // Default - send interest
    return {
      backgroundColor: template.button_style === 'filled' ? template.accent_color : 'transparent',
      color: template.button_style === 'filled' ? '#ffffff' : template.accent_color,
      borderWidth: template.button_style === 'outline' ? '2px' : '0',
      borderColor: template.accent_color,
      borderStyle: 'solid',
      cursor: 'pointer'
    };
  };

  const buttonStyles = getButtonStyles();

  // Render the appropriate button icon
  const renderButtonIcon = () => {
    // Non-premium users see crown icon (except for pending interests)
    if (!isPremium && !isInterestSent) {
      return <Crown className="w-4 h-4" />;
    }
    if (isMutualMatch) return <MessageSquare className="w-4 h-4" />;
    if (isInterestSent) return <Clock className="w-4 h-4" />;
    return <Heart className="w-4 h-4" />;
  };

  // Render upgrade prompt modal
  const renderUpgradePrompt = () => {
    if (!showUpgradePrompt) return null;
    
    return (
      <div 
        className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
        onClick={(e) => {
          e.stopPropagation();
          setShowUpgradePrompt(false);
        }}
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6 relative animate-in fade-in zoom-in duration-200"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close button */}
          <button
            onClick={() => setShowUpgradePrompt(false)}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          
          {/* Icon */}
          <div className="w-16 h-16 bg-gradient-to-br from-amber-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
            <Crown className="w-8 h-8 text-white" />
          </div>
          
          {/* Content */}
          <h3 className="text-xl font-bold text-gray-900 text-center mb-2">
            Upgrade to Premium
          </h3>
          <p className="text-gray-600 text-center mb-6">
            Send unlimited interests and connect with your perfect match. Premium members get:
          </p>
          
          {/* Benefits */}
          <ul className="space-y-2 mb-6">
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <Heart className="w-4 h-4 text-coral-500" />
              <span>Send unlimited interests</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <MessageSquare className="w-4 h-4 text-mint-500" />
              <span>Message your matches</span>
            </li>
            <li className="flex items-center gap-2 text-sm text-gray-700">
              <Shield className="w-4 h-4 text-blue-500" />
              <span>View verified profiles</span>
            </li>
          </ul>
          
          {/* CTA Buttons */}
          <button
            onClick={handleUpgradeClick}
            className="w-full py-3 px-4 bg-gradient-to-r from-amber-400 to-amber-500 text-white font-semibold rounded-xl hover:from-amber-500 hover:to-amber-600 transition-all shadow-md hover:shadow-lg"
          >
            <Crown className="w-4 h-4 inline mr-2" />
            Upgrade Now
          </button>
          <button
            onClick={() => setShowUpgradePrompt(false)}
            className="w-full mt-2 py-2 text-gray-500 hover:text-gray-700 text-sm transition-colors"
          >
            Maybe Later
          </button>
        </div>
      </div>
    );
  };

  // All cards are now portrait mode with auto-adjusting images
  // Photo Overlay Layout - Portrait with full background image
  if (template.layout === 'photo_overlay') {
    return (
      <div
        className={`overflow-hidden transition-all cursor-pointer hover:scale-[1.02] ${getBorderRadiusClass()} ${getShadowClass()} ${className}`}
        style={{ aspectRatio: '3/4' }} // Portrait aspect ratio
        onClick={handleCardClick}
        data-testid={`profile-card-${profile.id || profile._id}`}
      >
        <div className="relative h-full">
          <img
            src={profileImage}
            alt={profileName}
            className="w-full h-full object-cover object-center"
            style={{ minHeight: '100%', minWidth: '100%' }}
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${profileName}&size=400&background=random`;
            }}
          />
          
          {/* Gradient Overlay */}
          <div 
            className="absolute inset-0"
            style={{
              background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.4) 40%, rgba(0,0,0,0) 70%)'
            }}
          />

          {/* Top Badges */}
          <div className="absolute top-3 left-3 flex items-center gap-2">
            {template.show_premium_badge && profile.isPremium && !hidePremiumBadge && (
              <div 
                className="px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium text-white"
                style={{ backgroundColor: template.accent_color }}
              >
                <Crown className="w-3 h-3" />
                Premium
              </div>
            )}
            {template.show_verified_badge && (profile.isVerified || profile.govIdVerified) && (
              <div className="px-2 py-1 bg-blue-500 rounded-full flex items-center gap-1 text-xs font-medium text-white">
                <Shield className="w-3 h-3" />
                Verified
              </div>
            )}
          </div>

          {/* Online Status */}
          {template.show_online_status && profile.isOnline && (
            <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 bg-green-500 rounded-full text-xs text-white font-medium">
              <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
              Online
            </div>
          )}

          {/* Shortlist Button - Top Right */}
          <button
            onClick={handleShortlistClick}
            disabled={shortlistLoading}
            data-testid={`shortlist-btn-${profile.id || profile._id}`}
            className={`absolute top-3 right-3 p-2 rounded-full transition-all ${
              shortlisted 
                ? 'bg-coral-500 text-white' 
                : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
            } ${template.show_online_status && profile.isOnline ? 'top-12' : 'top-3'}`}
          >
            <Heart 
              className={`w-5 h-5 ${shortlisted ? 'fill-current' : ''}`} 
            />
          </button>

          {/* Bottom Content */}
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <h3 
              className={`${getNameSizeClass()} ${getNameWeightClass()} mb-1`}
              style={{ color: template.name_color || '#ffffff' }}
            >
              {profileName}
            </h3>

            <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 ${getDetailSizeClass()}`} style={{ opacity: 0.9 }}>
              {enabledFields.map(field => {
                const Icon = getFieldIcon(field.field_id);
                const value = getFieldValue(field.field_id);
                if (!value) return null;
                return (
                  <div key={field.field_id} className="flex items-center gap-1">
                    <Icon className="w-3.5 h-3.5" style={{ color: template.accent_color }} />
                    <span>{value}</span>
                  </div>
                );
              })}
            </div>

            {template.show_interest_button && (
              <button
                onClick={handleInterestClick}
                disabled={sending || (isInterestSent && !isMutualMatch)}
                data-testid={`interest-btn-${profile.id || profile._id}`}
                className={`mt-3 w-full py-2.5 px-4 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-2`}
                style={{
                  ...buttonStyles,
                  backdropFilter: !isInterestSent && !isMutualMatch && template.button_style !== 'filled' ? 'blur(8px)' : 'none'
                }}
              >
                {renderButtonIcon()}
                {sending ? 'Sending...' : getButtonText()}
              </button>
            )}
          </div>
        </div>
        {renderUpgradePrompt()}
      </div>
    );
  }

  // Horizontal layout converted to Portrait Card with side info
  if (template.layout === 'horizontal') {
    return (
      <>
      <div
        className={`overflow-hidden transition-all cursor-pointer hover:shadow-lg ${getBorderRadiusClass()} ${getShadowClass()} ${className}`}
        style={{
          backgroundColor: template.background_color,
          borderWidth: `${template.border_width || 1}px`,
          borderColor: template.border_color,
          borderStyle: 'solid',
          aspectRatio: '3/4' // Portrait aspect ratio
        }}
        onClick={handleCardClick}
        data-testid={`profile-card-${profile.id || profile._id}`}
      >
        {/* Top Photo Section - 60% of card */}
        <div className="relative" style={{ height: '60%' }}>
          <img
            src={profileImage}
            alt={profileName}
            className="w-full h-full object-cover object-center"
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${profileName}&size=400&background=random`;
            }}
          />
          {/* Badges on photo */}
          <div className="absolute top-2 left-2 flex gap-2">
            {template.show_premium_badge && profile.isPremium && !hidePremiumBadge && (
              <div 
                className="px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium text-white"
                style={{ backgroundColor: template.accent_color }}
              >
                <Crown className="w-3 h-3" />
              </div>
            )}
            {template.show_verified_badge && (profile.isVerified || profile.govIdVerified) && (
              <div className="px-2 py-1 bg-blue-500 rounded-full flex items-center gap-1 text-xs font-medium text-white">
                <Shield className="w-3 h-3" />
              </div>
            )}
          </div>
          
          {/* Shortlist Button */}
          <button
            onClick={handleShortlistClick}
            disabled={shortlistLoading}
            data-testid={`shortlist-btn-${profile.id || profile._id}`}
            className={`absolute top-2 right-2 p-1.5 rounded-full transition-all ${
              shortlisted 
                ? 'bg-coral-500 text-white' 
                : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
            }`}
          >
            <Heart className={`w-4 h-4 ${shortlisted ? 'fill-current' : ''}`} />
          </button>
          
          {template.show_online_status && profile.isOnline && (
            <div 
              className="absolute bottom-2 right-2 w-4 h-4 rounded-full border-2"
              style={{ backgroundColor: '#22c55e', borderColor: template.background_color }}
            />
          )}
        </div>

        {/* Bottom Info Section - 40% of card */}
        <div className={`p-3 flex flex-col justify-between`} style={{ height: '40%' }}>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h3 
                className={`${getNameSizeClass()} ${getNameWeightClass()} truncate`}
                style={{ color: template.name_color || template.text_color }}
              >
                {profileName}
              </h3>
            </div>

            <div className={`mt-1 space-y-0.5 ${getDetailSizeClass()}`} style={{ color: template.text_color, opacity: 0.7 }}>
              {enabledFields.map(field => {
                const Icon = getFieldIcon(field.field_id);
                const value = getFieldValue(field.field_id);
                if (!value) return null;
                return (
                  <div key={field.field_id} className="flex items-center gap-1.5">
                    <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: template.accent_color }} />
                    <span className="truncate">{value}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {template.show_interest_button && (
            <button
              onClick={handleInterestClick}
              disabled={sending || (isInterestSent && !isMutualMatch)}
              data-testid={`interest-btn-${profile.id || profile._id}`}
              className={`w-full mt-2 px-3 py-2 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1`}
              style={buttonStyles}
            >
              {renderButtonIcon()}
              {sending ? '...' : getButtonText()}
            </button>
          )}
        </div>
      </div>
      {renderUpgradePrompt()}
      </>
    );
  }

  // Default Vertical Layout - Portrait with auto-adjusting image
  return (
    <>
    <div
      className={`overflow-hidden transition-all cursor-pointer hover:shadow-lg hover:-translate-y-1 ${getBorderRadiusClass()} ${getShadowClass()} ${className}`}
      style={{
        backgroundColor: template.background_color,
        borderWidth: `${template.border_width || 1}px`,
        borderColor: template.border_color,
        borderStyle: 'solid',
        aspectRatio: '3/4' // Portrait aspect ratio
      }}
      onClick={handleCardClick}
      data-testid={`profile-card-${profile.id || profile._id}`}
    >
      {/* Photo Section - Top 55% */}
      <div className="relative" style={{ height: '55%' }}>
        <img
          src={profileImage}
          alt={profileName}
          className={`w-full h-full object-cover object-center ${getPhotoShapeClass()}`}
          onError={(e) => {
            e.target.src = `https://ui-avatars.com/api/?name=${profileName}&size=400&background=random`;
          }}
        />
        
        {/* Online Status */}
        {template.show_online_status && profile.isOnline && (
          <div 
            className="absolute bottom-2 right-2 w-4 h-4 rounded-full border-2"
            style={{ backgroundColor: '#22c55e', borderColor: template.background_color }}
          />
        )}

        {/* Badges on photo */}
        <div className="absolute top-2 left-2 flex gap-2">
          {template.show_premium_badge && profile.isPremium && !hidePremiumBadge && (
            <div 
              className="px-2 py-1 rounded-full flex items-center gap-1 text-xs font-medium text-white"
              style={{ backgroundColor: template.accent_color }}
            >
              <Crown className="w-3 h-3" />
            </div>
          )}
          {template.show_verified_badge && (profile.isVerified || profile.govIdVerified) && (
            <div className="px-2 py-1 bg-blue-500 rounded-full flex items-center gap-1 text-xs font-medium text-white">
              <Shield className="w-3 h-3" />
            </div>
          )}
        </div>
        
        {/* Shortlist Button */}
        <button
          onClick={handleShortlistClick}
          disabled={shortlistLoading}
          data-testid={`shortlist-btn-${profile.id || profile._id}`}
          className={`absolute top-2 right-2 p-1.5 rounded-full transition-all ${
            shortlisted 
              ? 'bg-coral-500 text-white' 
              : 'bg-white/20 backdrop-blur-sm text-white hover:bg-white/30'
          }`}
        >
          <Heart className={`w-4 h-4 ${shortlisted ? 'fill-current' : ''}`} />
        </button>
      </div>

      {/* Content Section - Bottom 45% */}
      <div className={`p-3 flex flex-col justify-between`} style={{ height: '45%' }}>
        <div>
          <div className="flex items-center gap-2">
            <h3 
              className={`${getNameSizeClass()} ${getNameWeightClass()} truncate`}
              style={{ color: template.name_color || template.text_color }}
            >
              {profileName}
            </h3>
          </div>

          <div className={`mt-1 space-y-0.5 ${getDetailSizeClass()}`} style={{ color: template.text_color, opacity: 0.7 }}>
            {enabledFields.map(field => {
              const Icon = getFieldIcon(field.field_id);
              const value = getFieldValue(field.field_id);
              if (!value) return null;
              return (
                <div key={field.field_id} className="flex items-center gap-1.5">
                  <Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: template.accent_color }} />
                  <span className="truncate">{value}</span>
                </div>
              );
            })}
          </div>

          {/* Bio */}
          {template.show_bio && profile.about && (
            <p 
              className={`mt-1 ${getDetailSizeClass()}`}
              style={{ 
                color: template.text_color, 
                opacity: 0.6,
                display: '-webkit-box',
                WebkitLineClamp: template.bio_max_lines || 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden'
              }}
            >
              {profile.about}
            </p>
          )}
        </div>

        {/* Action Button */}
        {template.show_interest_button && (
          <button
            onClick={handleInterestClick}
            disabled={sending || (isInterestSent && !isMutualMatch)}
            data-testid={`interest-btn-${profile.id || profile._id}`}
            className={`w-full py-2 px-4 ${getBorderRadiusClass()} text-sm font-medium transition-colors flex items-center justify-center gap-2`}
            style={buttonStyles}
          >
            {renderButtonIcon()}
            {sending ? 'Sending...' : getButtonText()}
          </button>
        )}
      </div>
    </div>
    {renderUpgradePrompt()}
    </>
  );
};

export default DynamicProfileCard;
